import { LinkIcon, PencilSquareIcon, TrashIcon, RectangleGroupIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import type { Model } from '../../api';
import { isModelActive } from '../../constants/modelSurfaces';
import Tooltip from '../common/Tooltip';
import StatusTag from '../common/StatusTag';
import { modelSymbol } from './modelIcon';

interface ModelCardProps {
  model: Model;
  onAttach?: (model: Model) => void;
  onEdit?: (model: Model) => void;
  onDelete?: (model: Model) => void;
}

/** Accuracy as a 0–100 number, or null when the registry doesn't track one. */
function accuracyPct(model: Model): number | null {
  const raw = model.accuracy;
  const n = typeof raw === 'string' ? parseFloat(raw) : typeof raw === 'number' ? raw : NaN;
  if (!Number.isFinite(n)) return null;
  // Tolerate both 0–1 ratios and 0–100 percentages.
  const pct = n <= 1 ? n * 100 : n;
  return Math.max(0, Math.min(100, pct));
}

export default function ModelCard({ model, onAttach, onEdit, onDelete }: ModelCardProps) {
  const groupCount = model.group_count ?? 0;
  const cameraCount = model.camera_count ?? 0;
  const active = isModelActive(model);
  const accuracy = accuracyPct(model);
  // Symbol is inferred from the model's name — the registry has no type column.
  const { Icon, tile, glyph, label: kind } = modelSymbol(model.name);

  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card p-5 shadow-card transition hover:shadow-card-hover">
      {/* Header: icon + name/description */}
      <div className="flex items-start gap-3">
        <Tooltip label={kind}>
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile}`}>
            <Icon className={`h-5 w-5 ${glyph}`} />
          </div>
        </Tooltip>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[13px] font-semibold text-foreground" title={model.name}>{model.name}</h3>
          {model.description && (
            <p className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">{model.description}</p>
          )}
        </div>
      </div>

      {/* Accuracy — label left, figure right, bar beneath. Only shown when the registry
          actually reports one; there is no placeholder for models that don't. */}
      {accuracy !== null && (
        <div className="mt-4">
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] text-muted-foreground">Accuracy</span>
            <span className="text-[13px] font-semibold text-brand tabular-nums">{accuracy.toFixed(1)}%</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-brand" style={{ width: `${accuracy}%` }} />
          </div>
        </div>
      )}

      {/* Status — dot + uppercase label, mirroring the ACTIVE / INACTIVE pill in the design.
          ACTIVE means the model is running on at least one camera (see isModelActive), so the
          pill is titled with the reason — otherwise "Inactive" next to a model nobody switched
          off reads as a fault. */}
      <div className="mt-3 flex items-center">
        <StatusTag
          tone={active ? 'success' : 'neutral'}
          title={
            active
              ? `Running on ${cameraCount} camera${cameraCount !== 1 ? 's' : ''}`
              : model.is_active === false
                ? 'Switched off in the registry'
                : 'Not attached to any camera'
          }
        >
          {active ? 'Active' : 'Inactive'}
        </StatusTag>
      </div>

      {/* Usage chips + action icon buttons on one line */}
      <div className="mt-auto flex items-center gap-2 pt-4">
        <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
          <RectangleGroupIcon className="h-3 w-3" />
          {groupCount} group{groupCount !== 1 ? 's' : ''}
        </span>
        <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
          <VideoCameraIcon className="h-3 w-3" />
          {cameraCount} camera{cameraCount !== 1 ? 's' : ''}
        </span>

        <div className="ml-auto flex items-center gap-1">
          {onAttach && (
            <Tooltip label="Attach to camera">
              <button
                type="button"
                aria-label="Attach to camera"
                onClick={() => onAttach(model)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brand transition-colors hover:bg-brand-light"
              >
                <LinkIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip label="Edit model">
              <button
                type="button"
                aria-label="Edit model"
                onClick={() => onEdit(model)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip label="Delete model">
              <button
                type="button"
                aria-label="Delete model"
                onClick={() => onDelete(model)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-destructive transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
